import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { useSignal } from "../net/signal";
import { Overlay } from "../ui/Overlay";
import { fa } from "./cards";
import { botBare } from "./seats";
import { BreakNext } from "./BreakNext";
import { nextLabel } from "./nextLabel";
import { seatColor, type DominoLocal } from "../board/state";
import type { BoardOnline } from "../board/controller";
import { BoardChatButtons, RoundSeat, useSeatBubbles, type SeatBubble } from "./RoundSeat";
import { fitChain, poseIfPlayed, splitChain, type Felt, type Fit, type Pose } from "../board/dominoGeom";

/**
 * میزِ دومینو — همتای DominoView ِ اپ (کاربر، ۲۵ اکتبر ۲۰۲۶: «میز و کارکرد را مثلِ این عکس‌ها کن» —
 * فقط میز و حس، قاعده همان).
 *
 * حریف‌ها بالا با چهره‌ی گرد، قرصِ نام، «۴۱ از ۱۰۰» و نشانِ شمارِ مهره‌ها؛ میزِ چوبی با نمدِ ویژه‌ی دومینو
 * («زمرد»، tools/domino_felt_art.py — نه فرشِ طرحِ میز)؛ زنجیره‌ی مار روی نمد (dominoGeom) با جفتِ چلیپا و مهره‌ی آخرِ طلایی؛
 * دستِ درشت؛ و ردیفِ پایین: چهره‌ی تو با حلقه‌ی ثانیه‌شمار، کپه‌ی بازار (لمس = بکش)، ایموجی و چت.
 * مهره‌ای که به هر دو سر می‌خورد، سایه‌اش را روی هر دو سر می‌گذارد و لمسِ سایه همان‌جا می‌نشاندش.
 */


/** خال‌ها، y رو به بالا — همان جدولِ اپ. */
const PIPS: [number, number][][] = [
  [], [[0, 0]], [[-1, 1], [1, -1]], [[-1, 1], [0, 0], [1, -1]], [[-1, 1], [1, 1], [-1, -1], [1, -1]],
  [[-1, 1], [1, 1], [0, 0], [-1, -1], [1, -1]], [[-1, 1], [1, 1], [-1, 0], [1, 0], [-1, -1], [1, -1]],
];

type Look = "" | "dim" | "last" | "ghost";

/** یک مهره در SVG (y رو به پایین)، مرکزِ (۰، ۰). v1 = نیمه‌ی چپ/بالا. */
function TileArt({ S, vert, v1, v2, look = "" }: { S: number; vert: boolean; v1: number; v2: number; look?: Look }) {
  // تورفتگیِ ۱ پیکسل روی میز؛ مهره‌ی دست در viewBox ِ ۱×۲ است و همان را به نسبت می‌گیرد
  const w = vert ? S : 2 * S, h = vert ? 2 * S : S, rx = S * 0.16, e = Math.min(1, S * 0.03);
  const half = (v: number, cx: number, cy: number, k: string) => (PIPS[v] ?? []).map(([px, py], i) =>
    <circle key={k + i} cx={cx + px * S * 0.25} cy={cy - py * S * 0.25} r={S * 0.09} />);
  return (
    <g className={`dm-t${look ? ` ${look}` : ""}`}>
      {look !== "ghost" && <rect className="sh" x={-w / 2 + e} y={-h / 2 + e + S * 0.08} width={w - 2 * e} height={h - 2 * e} rx={rx} />}
      <rect className="face" x={-w / 2 + e} y={-h / 2 + e} width={w - 2 * e} height={h - 2 * e} rx={rx} />
      {vert ? <line className="mid" x1={-S * 0.37} y1={0} x2={S * 0.37} y2={0} /> : <line className="mid" x1={0} y1={-S * 0.37} x2={0} y2={S * 0.37} />}
      <circle className="pin" r={S * 0.06} />
      <g className="pips">
        {vert ? [...half(v1, 0, -S / 2, "a"), ...half(v2, 0, S / 2, "b")] : [...half(v1, -S / 2, 0, "a"), ...half(v2, S / 2, 0, "b")]}
      </g>
    </g>
  );
}

/** مهره‌ی ایستاده‌ی دست (یا پشتِ آن برای تماشاچی) — viewBox ِ ۱×۲. */
function HandTile({ a, b, look = "", back = false }: { a: number; b: number; look?: Look; back?: boolean }) {
  return (
    <svg className="dm-ht" viewBox="-0.5 -1 1 2" aria-hidden="true">
      {back ? <BackArt w={1} h={2} /> : <TileArt S={1} vert v1={a} v2={b} look={look} />}
    </svg>
  );
}

/** پشتِ مهره: سرخِ لاکی، قابِ عاجی، ترنجِ طلایی. */
function BackArt({ w, h }: { w: number; h: number }) {
  const m = Math.min(w, h);
  return (
    <g className="dm-back">
      <rect className="face" x={-w / 2 + 0.02} y={-h / 2 + 0.02} width={w - 0.04} height={h - 0.04} rx={m * 0.16} />
      <rect className="inl" x={-w / 2 + m * 0.14} y={-h / 2 + m * 0.14} width={w - m * 0.28} height={h - m * 0.28} rx={m * 0.1} />
      <circle className="med" r={m * 0.2} /><circle className="dot" r={m * 0.08} />
    </g>
  );
}

/** اندازه‌ی واقعیِ یک جعبه — زنجیره با پیکسلِ فرش چیده می‌شود. jsdom اندازه ندارد: اندازه‌ی گوشی. */
function useBox(ref: RefObject<HTMLDivElement | null>): { w: number; h: number } {
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const r = el.getBoundingClientRect();
      setBox((b) => (Math.abs(b.w - r.width) < 0.5 && Math.abs(b.h - r.height) < 0.5 ? b : { w: r.width, h: r.height }));
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return box.w < 60 || box.h < 60 ? { w: 340, h: 420 } : box;
}

/** یک صندلی دومینو: صندلیِ گرد با امتیاز و (حریف) شمارِ مهره‌های دستش. */
function Seat({ m, seat, color, me, secs, frac, count, bubble }:
  { m: DominoLocal; seat: number; color: string; me: boolean; secs: number; frac: number; count?: number; bubble?: SeatBubble }) {
  const s = m.seats[seat];
  return (
    <RoundSeat name={me ? "تو" : botBare(s?.name || "حریف", s?.bot)} avatar={s?.avatar} country={s?.country} bot={s?.bot} me={me} color={color}
               turn={!m.over && !m.roundOver && m.turn === seat} secs={secs} frac={frac} bubble={bubble} pid={s?.playerId}
               sub={`${fa(m.score[seat] ?? 0)} از ۱۰۰`}
               extra={count !== undefined && (
                 <span className="cnt" aria-label={`${fa(count)} مهره`}>
                   <svg viewBox="-0.6 -1.05 1.2 2.1" aria-hidden="true"><g transform="rotate(-14) translate(.18 .05)"><BackArt w={1} h={1.6} /></g></svg>
                   <b>{fa(count)}</b>
                 </span>
               )} />
  );
}

export function Domino({ m, c }: { m: DominoLocal; c: BoardOnline }) {
  const side = useSignal(c.dominoSide);
  const last = useSignal(c.dominoLast);
  const roundOver = useSignal(c.roundOver);
  const timer = useSignal(c.timer);
  const feltRef = useRef<HTMLDivElement>(null);
  const box = useBox(feltRef);
  const bubbles = useSeatBubbles(m.n, m.me);
  const col = (s: number) => seatColor(s, m.shift, m.n);
  const opps = Array.from({ length: m.n - 1 }, (_, i) => i + 1);
  const playable = new Set(m.plays.map((p) => p.index));
  const secs = timer && m.mine ? Math.max(0, Math.ceil(timer.left)) : 0;
  const frac = timer && m.mine ? Math.max(0, Math.min(1, timer.left / Math.max(0.1, timer.total))) : 1;

  // زنجیره روی نمد؛ کاشیِ گره و نشانِ وسط به اندازه‌ی نمد
  const r = Math.round(Math.min(box.w, box.h) * 0.16);
  const tile = Math.round(Math.max(40, Math.min(box.w, box.h) * 0.13));
  const emblem = Math.round(Math.min(box.w, box.h) * 0.62);
  const felt: Felt = { w: box.w, h: box.h, r };
  const chain = splitChain(m.chain, m.center);
  const fit: Fit = fitChain(felt, chain);
  const lastIdx = lastPose(m, fit.poses, last);
  const pick = side >= 0 && m.mine ? m.hand[side] : undefined;
  const ghosts = pick ? [0, 1].map((sd) => ({ sd, ...poseIfPlayed(felt, chain, pick[0], pick[1], sd, m.leftEnd, m.rightEnd, fit) })) : [];
  const X = (p: Pose) => box.w / 2 + p.x, Y = (p: Pose) => box.h / 2 - p.y;

  const status = pick ? "کدام سر؟ روی سایه‌ی مهره بزن"
    : m.canDraw ? "مهره‌ی جور نداری — روی بازار بزن"
    : m.mine ? "یکی از مهره‌های روشن را بگذار"
    : m.over || m.roundOver ? "" : `نوبت ${c.seatName(m.turn)}`;

  // دست: مهره‌های ایستاده‌ی درشت؛ تماشاچی پشتشان را می‌بیند
  const handN = m.watching ? (m.counts[0] ?? 0) : m.hand.length;

  return (
    <div className="dm" style={{ "--hn": Math.max(5, handN) } as CSSProperties}>
      <div className={`dm-opps n${opps.length}`}>
        {opps.map((s) => (
          <Seat key={s} m={m} seat={s} color={col(s)} me={false} secs={0} frac={1} count={m.counts[s] ?? 0} bubble={bubbles.get(s)} />
        ))}
      </div>

      <div className="dm-table" style={{ "--r": `${r}px` } as CSSProperties}>
        <div className="dm-felt" ref={feltRef} dir="ltr" style={{ borderRadius: r, "--gt": `${tile}px` } as CSSProperties}>
          <i className="dm-rug" aria-hidden="true" />
          <i className="dm-frame" aria-hidden="true" />
          {["tl", "tr", "bl", "br"].map((k) => <i key={k} className={`dm-star ${k}`} aria-hidden="true" />)}
          <img className="dm-emblem" src="/art/domino/emblem.svg" alt="" style={{ inlineSize: emblem, blockSize: emblem }} />
          <svg className="dm-chain" viewBox={`0 0 ${box.w} ${box.h}`} role="img"
               aria-label={m.chain.length ? `زنجیره: ${m.chain.map(([a, b]) => `${fa(a)}|${fa(b)}`).join("، ")}` : "میز خالی است"}>
            {fit.poses.map((p, i) => (
              <g key={`${p.side}:${p.index}`} className={`dm-pos${i === lastIdx ? " last" : ""}`}
                 style={{ transform: `translate(${X(p)}px, ${Y(p)}px)` }}>
                {i === lastIdx && <rect className="glow" x={-(p.vert ? fit.S : 2 * fit.S) / 2 - 8} y={-(p.vert ? 2 * fit.S : fit.S) / 2 - 8}
                                        width={(p.vert ? fit.S : 2 * fit.S) + 16} height={(p.vert ? 2 * fit.S : fit.S) + 16} rx={fit.S * 0.3} />}
                <TileArt S={fit.S} vert={p.vert} v1={p.v1} v2={p.v2} look={i === lastIdx ? "last" : ""} />
              </g>
            ))}
            {ghosts.map(({ sd, pose, S }) => (
              <g key={`g${sd}`} className="dm-ghost" role="button" tabIndex={0}
                 aria-label={sd === 0 ? `سر چپ · ${fa(m.leftEnd)}` : `سر راست · ${fa(m.rightEnd)}`}
                 style={{ transform: `translate(${X(pose)}px, ${Y(pose)}px)` }}
                 onClick={() => c.dominoPick(sd)}
                 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); c.dominoPick(sd); } }}>
                <rect className="hit" x={-(pose.vert ? S : 2 * S) / 2 - 10} y={-(pose.vert ? 2 * S : S) / 2 - 10}
                      width={(pose.vert ? S : 2 * S) + 20} height={(pose.vert ? 2 * S : S) + 20} rx={S * 0.3} />
                <TileArt S={S} vert={pose.vert} v1={pose.v1} v2={pose.v2} look="ghost" />
              </g>
            ))}
          </svg>
          {m.chain.length === 0 && <div className="dm-empty">میز خالی است</div>}
        </div>
      </div>

      <div className="dm-status" aria-live="polite">{status}</div>

      <div className="dm-hand" dir="ltr">
        {m.watching
          ? Array.from({ length: handN }, (_, i) => <span key={i} className="dm-hbtn"><HandTile a={0} b={0} back /></span>)
          : m.hand.map(([a, b], i) => {
            const can = m.mine && playable.has(i);
            return (
              <button key={i} className={`dm-hbtn${can ? " can" : ""}${side === i ? " sel" : ""}`} onClick={() => c.dominoTile(i)}
                      aria-label={`مهره‌ی ${fa(a)}|${fa(b)}`} disabled={!m.mine}>
                <HandTile a={a} b={b} look={m.mine && !can ? "dim" : ""} />
              </button>
            );
          })}
      </div>

      <div className="dm-bar" dir="ltr">
        <Seat m={m} seat={0} color={col(0)} me={!m.watching} secs={secs} frac={frac} bubble={bubbles.get(0)} />
        <button className={`dm-yard${m.canDraw ? " on" : ""}`} onClick={() => c.dominoDraw()} disabled={!m.canDraw}
                aria-label={m.canDraw ? `بازار — بکش (${fa(m.yard)})` : `بازار: ${fa(m.yard)}`}>
          {m.yard > 0 && (
            <svg viewBox="-1.1 -1.15 2.2 2.3" aria-hidden="true">
              <g transform="translate(-.28 .06) rotate(10)"><BackArt w={0.9} h={1.6} /></g>
              <g transform="translate(.26 .08) rotate(-8)"><BackArt w={0.9} h={1.6} /></g>
              <g transform="translate(0 .02)"><BackArt w={0.9} h={1.6} /></g>
            </svg>
          )}
          <b className="n">{fa(m.yard)}</b>
          <span className="lbl">بازار</span>
          {m.canDraw && <span className="tag">بکش</span>}
        </button>
        <BoardChatButtons onReact={(k) => c.react(k)} />
      </div>

      {roundOver && !m.over && <RoundOver m={m} c={c} />}
    </div>
  );
}

/** اندیسِ مهره‌ی آخر در poses (−۱ = هیچ) — همان LastPose ِ اپ. */
function lastPose(m: DominoLocal, poses: Pose[], last: { side: number; a: number; b: number } | null): number {
  if (!last || m.chain.length === 0) return -1;
  const same = ([x, y]: [number, number]) => (x === last.a && y === last.b) || (x === last.b && y === last.a);
  const leftN = m.center, rightN = m.chain.length - 1 - m.center;
  for (let i = 0; i < poses.length; i++) {
    const p = poses[i]!;
    if (last.side < 0 && p.side === -1 && m.chain.length === 1 && same(m.chain[0]!)) return i;
    if (last.side === 1 && p.side === 1 && p.index === rightN - 1 && same(m.chain[m.chain.length - 1]!)) return i;
    if (last.side === 0 && p.side === 0 && p.index === leftN - 1 && same(m.chain[0]!)) return i;
  }
  return -1;
}

function RoundOver({ m, c }: { m: DominoLocal; c: BoardOnline }) {
  const who = m.roundWinner >= 0 ? c.seatName(m.roundWinner) : "";
  const title = m.roundWinner < 0 ? "دور بسته شد — مساوی"
    : `${m.blocked ? "دور بسته شد — " : ""}${who} این دور را برد · +${fa(m.roundPoints)}`;
  return (
    <Overlay label="پایان دور" className="minor" card={m.watching || m.roundWinner < 0 ? "even" : m.roundWinner === 0 ? "win" : "lose"}>
      <div className="vt">{title}</div>
      <div className="stack" style={{ width: "100%", maxWidth: 320 }}>
        {m.seats.map((_, s) => (
          <div key={s} className="row" style={{ justifyContent: "space-between", fontWeight: s === m.roundWinner ? 700 : 400 }}>
            <span>{c.seatName(s)}</span><span>{fa(m.score[s] ?? 0)} از ۱۰۰</span>
          </div>
        ))}
      </div>
      <BreakNext watch={c.brk} label={nextLabel("دور", m.watching || m.roundWinner < 0 ? "even" : m.roundWinner === 0 ? "win" : "lose")}
                 onNext={() => c.nextRound()} />
    </Overlay>
  );
}

